/**
 * CUB Set Bundle Swap
 *
 * On CUB "Set" product pages, intercepts Add to Cart and adds the component
 * products instead (1x matching table + 2x matching stool).
 *
 * The Set product itself never enters the cart — customers see 3 line items.
 * An automatic discount rule applies $50 off when the bundle combo is present,
 * so the effective total matches the original $750 set price.
 */
(function () {
  'use strict';

  // Map: Set product handle → array of component variants to add
  // Each variant has a Shopify variant ID and the quantity to add.
  const CUB_SET_MAP = {
    'victoria-bitter-retro-bar-stool-and-table-set': [
      { id: 42810874429579, quantity: 1 }, // VB-TABLE
      { id: 42810314162315, quantity: 2 }, // VB-STOOL
    ],
    'carlton-draught-retro-bar-stool-and-table-set': [
      { id: 42810880950411, quantity: 1 }, // C-DRAUGHT-TABLE
      { id: 42810321010827, quantity: 2 }, // C-DRAUGHT-STOOL
    ],
    'carlton-dry-retro-bar-stool-and-table-set': [
      { id: 42810930921611, quantity: 1 }, // C-DRY-TABLE
      { id: 42810347585675, quantity: 2 }, // C-DRY-STOOL
    ],
    'great-northern-retro-bar-stool-and-table-set': [
      { id: 42810934362251, quantity: 1 }, // GN-TABLE
      { id: 42810355286155, quantity: 2 }, // GN-STOOL
    ],
    'reschs-retro-bar-stool-and-table-set': [
      { id: 47080024768651, quantity: 1 }, // RESCHS-TABLE
      { id: 47080024703115, quantity: 2 }, // RESCHS-STOOL
    ],
    'melbourne-bitter-retro-bar-stool-and-table-set': [
      { id: 47080024014987, quantity: 1 }, // MB-TABLE
      { id: 47096909594763, quantity: 2 }, // MB-STOOL
    ],
    'corona-retro-bar-stool-and-table-set': [
      { id: 47096902615179, quantity: 1 }, // CORONA-TABLE
      { id: 47096909693067, quantity: 2 }, // CORONA-STOOL
    ],
    'fosters-retro-bar-stool-and-table-set': [
      { id: 47080022769803, quantity: 1 }, // FOSTERS-TABLE
      { id: 47096911593611, quantity: 2 }, // FOSTERS-STOOL
    ],
  };

  // Extract the current product handle from the URL.
  function getCurrentHandle() {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  // Given a product form, mark it as a bundle and intercept its submit.
  function attachBundleInterceptor(form, items) {
    if (form.dataset.cubBundleAttached === '1') return;
    form.dataset.cubBundleAttached = '1';

    form.addEventListener(
      'submit',
      async (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const submitter =
          event.submitter ||
          form.querySelector('[type="submit"]') ||
          form.querySelector('button[name="add"]');

        if (submitter?.hasAttribute('aria-disabled')) return;

        submitter?.setAttribute('aria-disabled', 'true');
        submitter?.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ items }),
          });

          if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.description || 'Failed to add bundle to cart');
          }

          // Fetch updated cart for theme listeners
          const cartResp = await fetch('/cart.js');
          const cart = await cartResp.json();

          // Let the theme know the cart updated
          if (window.theme?.pubsub?.publish && window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
            window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'cub-set-bundle',
              cart,
            });
          }

          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true })
          );
          document.dispatchEvent(
            new CustomEvent('ajaxProduct:added', {
              detail: { product: cart },
            })
          );

          // Try opening the cart drawer, fall back to /cart
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.show === 'function') {
            drawer.show(submitter);
          } else {
            window.location.href = '/cart';
          }
        } catch (err) {
          console.error('[cub-set-bundle] Error adding bundle:', err);
          document.dispatchEvent(
            new CustomEvent('ajaxProduct:error', {
              detail: { errorMessage: err.message },
            })
          );
          alert(err.message || 'Sorry, something went wrong adding the bundle to your cart.');
        } finally {
          submitter?.removeAttribute('aria-busy');
          submitter?.removeAttribute('aria-disabled');
        }
      },
      true // capture phase so we run before the default handler
    );
  }

  function init() {
    const handle = getCurrentHandle();
    if (!handle) return;

    const items = CUB_SET_MAP[handle];
    if (!items || !items.length) return;

    // Mark the body so Liquid / CSS can react if needed
    document.body.setAttribute('data-cub-set-bundle', handle);

    // Attach to all existing product add-to-cart forms
    document
      .querySelectorAll('form[action*="/cart/add"]')
      .forEach((form) => attachBundleInterceptor(form, items));

    // Handle dynamically-injected forms (e.g. cart drawer reopens)
    const observer = new MutationObserver(() => {
      document
        .querySelectorAll('form[action*="/cart/add"]')
        .forEach((form) => attachBundleInterceptor(form, items));
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
